// Office review and completion (technical design §6.4, M6; office-review prototype). Technicians
// pause; the office checks the work and completes the job. The checklist comes from the server
// (jobs.reviewChecklist), so the Complete button and the rule cannot disagree. Prices a technician
// set below their limit wait here for the office's OK too (PriceOks.tsx).
// BL-189: on ListPage. What waits for the office is the list (Awaiting review), with the jobs sent back
// and the flags under it; what's settled is beside it (Completed recently), with the prices waiting for
// an OK and what signed-out iPads sent over it. Every job is a RowList row whose name opens it; where it
// stands is the word at its end.

import { useEffect, useRef, useState } from 'react';
import { dateTimeText } from '@flowology/kernel';
import { RpcError } from '@flowology/client';
import { RowList, type Row } from '@flowology/ui-web/fl';
import {
  ListPage,
  Section,
  type ListRegion,
  type ListStatus,
  type PageHeadProps,
} from '@flowology/ui-web/patterns';
import { PriceOksSection } from './PriceOks.tsx';
import { liveStream } from '../connection/live.ts';
import { client } from '../api.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import {
  flagText,
  queueChip,
  REVIEW_EVENTS,
  sentBackWhen,
  reviewRights,
  addDonePage,
  addQueuePage,
  doneInput,
  donePage,
  doneRereadLimit,
  QUEUE_PAGE,
  queuePages,
  type DoneJob,
  type DonePage,
  type FlagPage,
  type OfficeFlag,
  type QueueJob,
  type QueuePage,
} from './review-model.ts';
import { rememberedQueue, rememberQueue } from './queue-cache.ts';
import { shownText } from '../shared/pages.ts';
import { openJob } from '../job/job-page.ts';
import { QuarantineSection } from './Quarantine.tsx';
import { useMirror } from '../shared/mirror.ts';
import { plainText } from '../shared/plain-text.ts';
import { addRows } from '../blueprint/paged.ts';
import './review-page.css';

const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';

export function ReviewScreen({ nav, timeZone }: { nav: NavProps; timeZone: string }) {
  const [tick, setTick] = useState(0);
  const mirroring = useMirror();
  const rights = reviewRights(nav.permissions ?? [], mirroring);
  const crumb = usePageCrumb(nav);

  // Live: re-read when the server says a job, visit, material or flag changed.
  useEffect(() => {
    const es = liveStream();
    es.addEventListener('change', (e) => {
      const { type } = JSON.parse((e as MessageEvent<string>).data) as { type: string };
      if (REVIEW_EVENTS.test(type)) setTick((t) => t + 1);
    });
    return () => es.close();
  }, []);

  // A job opens on its one page (job/JobRoute.tsx), over this queue: its Review area is where it's
  // reviewed and completed.
  return (
    <div className="app">
      <main>
        <PageBanners nav={nav} />
        <Queue
          head={{
            ...(crumb ? { crumb } : {}),
            title: 'Review queue',
            lede: 'Techs pause; the office checks each job, then completes it.',
          }}
          tenant={nav.tenant}
          tick={tick}
          timeZone={timeZone}
          rights={rights}
        />
      </main>
    </div>
  );
}

function Queue({
  head,
  tenant,
  tick,
  timeZone,
  rights,
}: {
  head: PageHeadProps;
  tenant: string;
  tick: number;
  timeZone: string;
  rights: ReturnType<typeof reviewRights>;
}) {
  // Coming back from a job, the last queue shows at once ("Updated 10:12") while it is re-read.
  const kept = rememberedQueue(tenant);
  const [queue, setQueue] = useState<QueuePage | null>(kept?.queue ?? null);
  const [back, setBack] = useState<QueuePage>(kept?.back ?? NO_JOBS);
  const [done, setDone] = useState<DonePage | null>(kept?.done ?? null);
  const [fresh, setFresh] = useState(!kept);
  const [at, setAt] = useState(kept?.at ?? null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [doneFailed, setDoneFailed] = useState(false);
  const moreBusy = useRef(false);
  const jobs = queue?.jobs ?? null;
  // How much of each list is open, so a refresh reads it all again and "Show more" stays open; a page
  // that comes back after a refresh began is dropped (the refresh has it).
  const shown = useRef({ queue: 0, back: 0, done: 0 });
  shown.current = { queue: jobs?.length ?? 0, back: back.jobs.length, done: done?.jobs.length ?? 0 };
  const reads = useRef(0);
  useEffect(() => {
    reads.current++;
    setError(null);
    setDoneFailed(false);
    readQueue({}, queuePages(shown.current.queue))
      .then((r) => {
        setQueue(r);
        setFresh(true);
        setAt(new Date().toISOString());
      })
      .catch((e: unknown) => setError(message(e)));
    readQueue({ sentBack: true }, queuePages(shown.current.back))
      .then(setBack)
      .catch(() => undefined);
    // the latest completed first, as many as were shown (list caps, BL-110)
    client
      .call<ViewAnswer>('views.run', doneInput(0, doneRereadLimit(shown.current.done)))
      .then((r) => setDone(donePage(r)))
      .catch(() => setDoneFailed(true));
  }, [tick, attempt]);
  useEffect(() => {
    if (queue && at && done) rememberQueue(tenant, { queue, back, done, at });
  });
  // the next page of a list; one at a time, so a second press doesn't read the same page twice
  const once = (read: () => Promise<void>) => {
    if (moreBusy.current) return;
    moreBusy.current = true;
    void read()
      .catch((e: unknown) => setError(message(e)))
      .finally(() => (moreBusy.current = false));
  };
  const more = (sentBack: boolean) => {
    const from = sentBack ? back.next : queue?.next;
    if (!from) return;
    const read = reads.current;
    once(() =>
      client
        .call<QueuePage>('jobs.reviewQueue', {
          ...(sentBack ? { sentBack } : {}),
          limit: QUEUE_PAGE,
          after: from,
        })
        .then((page) => {
          if (read !== reads.current) return;
          if (sentBack) setBack((p) => addQueuePage(p, page));
          else setQueue((p) => p && addQueuePage(p, page));
        }),
    );
  };
  // Completed recently's next page, from the jobs already shown
  const moreDone = () => {
    const read = reads.current;
    once(() =>
      client.call<ViewAnswer>('views.run', doneInput(done?.jobs.length ?? 0)).then((r) => {
        if (read === reads.current) setDone((p) => p && addDonePage(p, donePage(r)));
      }),
    );
  };

  const awaiting: ListRegion = {
    title: 'Awaiting review',
    ...(queue ? { count: queue.total } : {}),
    status: stateOf(jobs, jobs === null && error !== null),
    what: 'the review queue',
    emptyText: 'All caught up.',
    failedText: error ?? 'The review queue didn’t load.',
    onRetry: () => setAttempt((n) => n + 1),
    children: jobs && (
      <RowList
        label="Awaiting review"
        rows={jobs.map(queueRow)}
        {...(queue?.next ? { onMore: () => more(false) } : {})}
        {...(!fresh && kept ? { note: `Updated ${clockTime(kept.at, timeZone)}` } : {})}
      />
    ),
    after: (
      <>
        {back.jobs.length > 0 && (
          <Section title="Sent back, waiting on the tech" count={back.total}>
            <RowList
              label="Sent back, waiting on the tech"
              rows={back.jobs.map((j) => sentBackRow(j, timeZone))}
              {...(back.next ? { onMore: () => more(true) } : {})}
            />
          </Section>
        )}
        {rights.review && <FlagsSection tick={tick} timeZone={timeZone} />}
      </>
    ),
  };
  const completed: ListRegion = {
    title: 'Completed recently',
    status: done ? (done.jobs.length ? 'ready' : 'empty') : doneFailed ? 'failed' : 'loading',
    what: 'the jobs completed lately',
    emptyText: 'No job has been completed lately.',
    failedText: 'The jobs completed lately didn’t load.',
    onRetry: () => setAttempt((n) => n + 1),
    children: done && (
      <RowList
        label="Completed recently"
        rows={done.jobs.map(doneRow)}
        first={5}
        {...(done.more ? { onMore: moreDone, note: shownText(done.jobs.length, done.total) } : {})}
      />
    ),
    // what waits for the office's say beside the list: the prices to OK, what signed-out iPads sent
    before: (
      <>
        {/* estimates.approve's roles are review's: owner, manager, dispatcher (price-ok-model.ts) */}
        {rights.review && <PriceOksSection tick={tick} timeZone={timeZone} canOk={rights.close} />}
        {rights.quarantine && <QuarantineSection tick={tick} timeZone={timeZone} />}
      </>
    ),
  };
  return (
    <ListPage
      head={head}
      // a re-read or a next page that failed, with the list already shown
      {...(error && jobs !== null
        ? {
            notice: (
              <p className="rv__bad" role="alert">
                {error}
              </p>
            ),
          }
        : {})}
      list={awaiting}
      aside={completed}
    />
  );
}

const stateOf = (rows: readonly unknown[] | null, failed: boolean): ListStatus =>
  rows ? (rows.length ? 'ready' : 'empty') : failed ? 'failed' : 'loading';

/** "#4102 · Fairview residence": a job in the queue, as its row names it. */
const jobName = (number: string, customerName: string) => `#${number} · ${customerName}`;
const summaryLine = (summary: string) => ({ text: plainText(summary) });

/** A job waiting for review: ready to complete, or the first thing to fix (waiting, dashed under). */
function queueRow(j: QueueJob): Row {
  const chip = queueChip(j);
  return {
    id: j.jobId,
    name: jobName(j.number, j.customerName),
    onOpen: () => openJob(j.jobId),
    lines: [summaryLine(j.summary)],
    end: { word: chip.text, tone: chip.tone === 'warn' ? 'wait' : 'muted' },
  };
}

/** A job the office sent back: why, waiting on the tech, and when it went. */
function sentBackRow(j: QueueJob, timeZone: string): Row {
  return {
    id: j.jobId,
    name: jobName(j.number, j.customerName),
    onOpen: () => openJob(j.jobId),
    lines: [
      summaryLine(j.summary),
      ...(j.sentBackAt ? [{ text: `Sent ${sentBackWhen(j.sentBackAt, timeZone)}` }] : []),
    ],
    end: { word: `Sent back${j.sentBackReason ? `: ${j.sentBackReason}` : ''}`, tone: 'wait' },
  };
}

function doneRow(j: DoneJob): Row {
  return {
    id: j.id,
    name: jobName(j.number, j.customerName),
    onOpen: () => openJob(j.id),
    lines: [summaryLine(j.summary)],
    end: { word: 'Completed', tone: 'muted' },
  };
}

/** A flag on a job opens the job, where it's resolved; one on no job only says what it is. */
function flagRow(f: OfficeFlag, timeZone: string): Row {
  const jobId = f.jobId;
  return {
    id: f.id,
    name: f.jobNumber ? `#${f.jobNumber}${f.customerName ? ` · ${f.customerName}` : ''}` : 'A flag',
    ...(jobId ? { onOpen: () => openJob(jobId) } : {}),
    lines: [{ text: flagText(f.reason) }],
    end: { word: dateTimeText(f.createdAt, timeZone, { year: false }), tone: 'wait' },
  };
}

const NO_JOBS: QueuePage = { jobs: [], total: 0, next: null };
/** views.run's answer, as Completed recently reads it. */
type ViewAnswer = { rows: Record<string, unknown>[]; truncated: boolean; total?: number };

/** The queue's first `pages` pages, one after another: each starts where the one before ended. */
async function readQueue(input: { sentBack?: true }, pages: number): Promise<QueuePage> {
  const page = (after?: QueuePage['next']) =>
    client.call<QueuePage>('jobs.reviewQueue', { ...input, limit: QUEUE_PAGE, ...(after ? { after } : {}) });
  let all = await page();
  for (let i = 1; i < pages && all.next; i++) all = addQueuePage(all, await page(all.next));
  return all;
}

const clockTime = (iso: string, timeZone: string) =>
  new Intl.DateTimeFormat('en-CA', { hour: 'numeric', minute: '2-digit', timeZone })
    .format(new Date(iso))
    .replace(/\s?[ap]\.?m\.?$/i, '');

/** Open review flags across jobs (gap #18: reviewFlags.list); one opens its job, where it's resolved. */
function FlagsSection({ tick, timeZone }: { tick: number; timeZone: string }) {
  const [flags, setFlags] = useState<FlagPage | null>(null);
  const moreBusy = useRef(false);
  const reads = useRef(0);
  // only those the server places on a job (onJob): a flag the office can't open is no use here
  const page = (after?: string) =>
    client.call<FlagPage>('reviewFlags.list', { status: 'open', onJob: true, ...(after ? { after } : {}) });
  useEffect(() => {
    const read = ++reads.current;
    void page()
      .then((r) => read === reads.current && setFlags(r))
      .catch(() => read === reads.current && setFlags(null));
  }, [tick]);
  const more = () => {
    if (!flags?.next || moreBusy.current) return;
    const read = reads.current;
    moreBusy.current = true;
    void page(flags.next)
      .then(
        (r) =>
          read === reads.current &&
          setFlags((f) => f && { flags: addRows(f.flags, r.flags), total: r.total, next: r.next }),
      )
      .catch(() => undefined) // Show more stays, to try again
      .finally(() => (moreBusy.current = false));
  };
  if (!flags?.flags.length) return null;
  return (
    <Section title="Flagged for the office" count={flags.total}>
      <RowList
        label="Flagged for the office"
        rows={flags.flags.map((f) => flagRow(f, timeZone))}
        {...(flags.next ? { onMore: more } : {})}
      />
    </Section>
  );
}
